import { PaymentFormProps, PaymentMethod } from "../../../../types"
import paymentMethodValidator from "./paymentMethodValidator"
import { lookupUkBank } from "./ukBankLookup"
import React, { FC, useEffect, useMemo, useRef, useState } from "react"
import { connect } from "react-redux"
import { createPaymentMethod, deletePaymentMethod, updatePaymentMethod } from "../../../../actions"
import { KButton, KDropdown, KInput, KTitleSpan, KSpan } from "kahuna-base-react-components"
import Warning from "./Warning"
import { lang } from "../../../../constants/languages"
import { currencyOptions } from "../../../../constants"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import {
  CountryType,
  CountryOption,
  priorityCountries,
  otherCountries,
  nigeriaBankOptions,
  southAfricaBankOptions,
  saAccountTypeOptions,
  brazilAccountTypeOptions,
  philippinesBankOptions,
  japanAccountTypeOptions,
  resolveNZBankName
} from "./countryConfig"

const emptyPaymentMethod = (): PaymentMethod => ({
  bankName: "",
  iban: "",
  accountHolderName: "",
  swiftCode: "",
  currency: "",
  accountNumber: "",
  accountType: "",
  bankCode: "",
  branchName: "",
  countryType: "",
  taxId: ""
})

// The countries whose form identifies the account by IBAN. Everywhere else the number belongs in
// accountNumber, which is why an IBAN a form was opened with is not carried into one of those.
const IBAN_COUNTRIES: CountryType[] = ["tr", "eu"]

const PaymentForm: FC<PaymentFormProps> = (props) => {
  const {
    initialValues,
    editable,
    deletePaymentMethod,
    updatePaymentMethod,
    createPaymentMethod,
    setAdding,
    edit,
    setEdit,
    adminPanelUserId,
    title,
    addPaymentMethod,
    setAddPaymentMethod,
    chosenMethod,
    onSubmit
  } = props

  const isEditing = !!chosenMethod
  const [selectedCountry, setSelectedCountry] = useState<CountryType | null>(
    (chosenMethod?.countryType as CountryType) || null
  )

  const [countrySearch, setCountrySearch] = useState("")
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const filteredCountries = useMemo(() => {
    const query = countrySearch.toLowerCase().trim()
    if (!query) return { priority: priorityCountries, others: otherCountries }
    return {
      priority: priorityCountries.filter((c) => c.label.toLowerCase().includes(query)),
      others: otherCountries.filter((c) => c.label.toLowerCase().includes(query))
    }
  }, [countrySearch])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsCountryDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // A form can be opened with values already in it rather than empty: the co-manager reads an account
  // off a picture and hands it over here, where the artist picks the country and checks the fields the
  // country asks for. Editing a saved account still wins over that, it is the account itself.
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>({
    ...emptyPaymentMethod(),
    ...(chosenMethod || initialValues || {})
  })

  useEffect(() => {
    if (chosenMethod) {
      setPaymentMethod(chosenMethod)
      setSelectedCountry((chosenMethod.countryType as CountryType) || "eu")

      if (chosenMethod.countryType === "uk" && chosenMethod.iban && !chosenMethod.bankCode) {
        const cleanIban = chosenMethod.iban.replace(/\s/g, "")
        if (cleanIban.length >= 22 && cleanIban.startsWith("GB")) {
          const sortCodeFromIban = cleanIban.substring(8, 14)
          const accountNumberFromIban = cleanIban.substring(14, 22)
          const formattedSortCode = `${sortCodeFromIban.slice(0, 2)}-${sortCodeFromIban.slice(
            2,
            4
          )}-${sortCodeFromIban.slice(4, 6)}`
          setPaymentMethod((prev) => ({
            ...prev,
            bankCode: formattedSortCode,
            accountNumber: accountNumberFromIban
          }))
        }
      }
    }
  }, [chosenMethod])

  // Reset when dialog closes
  useEffect(() => {
    if (!addPaymentMethod && !edit) {
      setSelectedCountry(isEditing ? (chosenMethod?.countryType as CountryType) || null : null)
      setCountrySearch("")
      setIsCountryDropdownOpen(false)
    }
  }, [addPaymentMethod, edit])

  const handleCountrySelect = (country: CountryType) => {
    setSelectedCountry(country)
    // Choosing the country clears what the country decides and keeps what it does not, so a form
    // opened with values in it is not emptied by the first thing the artist does in it. The row being
    // edited comes along too, otherwise the accepted account would be saved as a second one.
    setPaymentMethod({
      ...emptyPaymentMethod(),
      id: initialValues?.id,
      user: initialValues?.user,
      bankName: initialValues?.bankName || "",
      accountHolderName: initialValues?.accountHolderName || "",
      swiftCode: initialValues?.swiftCode || "",
      currency: initialValues?.currency || "",
      iban: IBAN_COUNTRIES.includes(country) ? initialValues?.iban || "" : "",
      countryType: country
    })
  }

  // --- UK: auto-fill IBAN from sort code + account number ---

  useEffect(() => {
    if (selectedCountry !== "uk") return
    const cleanSort = (paymentMethod.bankCode || "").replace(/-/g, "")
    const accNum = paymentMethod.accountNumber || ""
    if (cleanSort.length === 6 && accNum.length === 8) {
      const result = lookupUkBank(cleanSort, accNum)
      if (result) {
        setPaymentMethod((prev) => ({
          ...prev,
          iban: result.iban,
          bankName: result.bankName || prev.bankName,
          swiftCode: result.bic || prev.swiftCode
        }))
      }
    }
  }, [selectedCountry, paymentMethod.bankCode, paymentMethod.accountNumber])

  // --- Formatters ---

  const formatSortCode = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 6)
    const formatted = cleaned.replace(/(\d{2})(?=\d)/g, "$1-").slice(0, 8)
    setPaymentMethod((prev) => ({ ...prev, bankCode: formatted }))
  }

  const formatAccountNumber = (value: string, maxLength?: number) => {
    const cleaned = value.replace(/\D/g, "").slice(0, maxLength || 20)
    setPaymentMethod((prev) => ({ ...prev, accountNumber: cleaned }))
  }

  const formatRoutingNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 9)
    setPaymentMethod((prev) => ({ ...prev, bankCode: cleaned }))
  }

  const formatBSB = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 6)
    const formatted = cleaned.length > 3 ? `${cleaned.slice(0, 3)}-${cleaned.slice(3)}` : cleaned
    setPaymentMethod((prev) => ({ ...prev, bankCode: formatted }))
  }

  const formatBranchCode = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 6)
    setPaymentMethod((prev) => ({ ...prev, bankCode: cleaned }))
  }

  const formatNUBAN = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 10)
    setPaymentMethod((prev) => ({ ...prev, accountNumber: cleaned }))
  }

  const formatIban = (rawValue: string) => {
    if (rawValue === "") {
      setPaymentMethod((prev) => ({ ...prev, iban: "" }))
      return
    }

    const regex = /^[A-Za-z0-9\s]*$/
    if (!regex.test(rawValue)) return

    const cleaned = rawValue.replace(/\s+/g, "").toUpperCase()
    const formattedIban = cleaned.replace(/(.{4})/g, "$1 ").trim()

    setPaymentMethod((prev) => ({ ...prev, iban: formattedIban }))
  }

  const formatCanadaTransitInstitution = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 8)
    const formatted = cleaned.length > 5 ? `${cleaned.slice(0, 5)}-${cleaned.slice(5)}` : cleaned
    setPaymentMethod((prev) => ({ ...prev, bankCode: formatted }))
  }

  const formatIFSC = (value: string) => {
    const cleaned = value
      .replace(/[^A-Za-z0-9]/g, "")
      .slice(0, 11)
      .toUpperCase()
    setPaymentMethod((prev) => ({ ...prev, bankCode: cleaned }))
  }

  const formatSGBankBranch = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 7)
    const formatted = cleaned.length > 4 ? `${cleaned.slice(0, 4)}-${cleaned.slice(4)}` : cleaned
    setPaymentMethod((prev) => ({ ...prev, bankCode: formatted }))
  }

  const formatBrazilBankCode = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 3)
    setPaymentMethod((prev) => {
      const existing = prev.bankCode || ""
      const agencyPart = existing.length >= 4 && existing[3] === "-" ? existing.substring(4) : ""
      const combined = agencyPart ? `${digits}-${agencyPart}` : digits
      return { ...prev, bankCode: combined }
    })
  }

  const formatBrazilAgency = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 5)
    const formatted = digits.length > 4 ? `${digits.slice(0, 4)}-${digits.slice(4)}` : digits
    setPaymentMethod((prev) => {
      const bankCodePart = (prev.bankCode || "").substring(0, 3)
      const combined = bankCodePart ? `${bankCodePart}-${formatted}` : formatted
      return { ...prev, bankCode: combined }
    })
  }

  const formatBrazilAccountNumber = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 9)
    const formatted = digits.length > 8 ? `${digits.slice(0, 8)}-${digits.slice(8)}` : digits
    setPaymentMethod((prev) => ({ ...prev, accountNumber: formatted }))
  }

  const formatCPFCNPJ = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 14)
    setPaymentMethod((prev) => ({ ...prev, taxId: cleaned }))
  }

  const formatPHBankCode = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 10)
    setPaymentMethod((prev) => ({ ...prev, accountNumber: cleaned }))
  }

  const formatNZAccount = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 16)
    let formatted = cleaned
    if (cleaned.length > 2) formatted = `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`
    if (cleaned.length > 6) formatted = `${cleaned.slice(0, 2)}-${cleaned.slice(2, 6)}-${cleaned.slice(6)}`
    if (cleaned.length > 13) {
      formatted = `${cleaned.slice(0, 2)}-${cleaned.slice(2, 6)}-${cleaned.slice(6, 13)}-${cleaned.slice(13)}`
    }
    const autoBank = resolveNZBankName(cleaned)
    setPaymentMethod((prev) => ({
      ...prev,
      accountNumber: formatted,
      bankName: autoBank || prev.bankName
    }))
  }


  // --- Local formats: Japan, Mexico, South Korea, Hong Kong, Malaysia, Indonesia, Thailand, Vietnam ---
  //
  // A bank code and a branch code share the one bankCode field, joined with a dash the way Canada,
  // Singapore and Brazil already join theirs, so the panel, the export and the payout all keep reading
  // one field per account rather than learning a new one per country.

  const splitBankBranch = (value?: string) => {
    const parts = (value || "").split("-")
    return { bank: parts[0] || "", branch: parts[1] || "" }
  }

  const setBankBranch = (bank: string, branch: string) => {
    setPaymentMethod((prev) => ({ ...prev, bankCode: branch ? `${bank}-${branch}` : bank }))
  }

  const formatJPBankCode = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 4)
    setBankBranch(cleaned, splitBankBranch(paymentMethod.bankCode).branch)
  }

  const formatJPBranchCode = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 3)
    setBankBranch(splitBankBranch(paymentMethod.bankCode).bank, cleaned)
  }

  const formatHKBankCode = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 3)
    setBankBranch(cleaned, splitBankBranch(paymentMethod.bankCode).branch)
  }

  const formatHKBranchCode = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 3)
    setBankBranch(splitBankBranch(paymentMethod.bankCode).bank, cleaned)
  }

  // Korea and Thailand are written with dashes in them wherever they are printed, so the dashes come
  // off as they are typed rather than being rejected back at the artist.
  const formatDigitsBankCode = (value: string, maxLength: number) => {
    const cleaned = value.replace(/\D/g, "").slice(0, maxLength)
    setPaymentMethod((prev) => ({ ...prev, bankCode: cleaned }))
  }

  const formatAlphanumericBankCode = (value: string, maxLength: number) => {
    const cleaned = value
      .replace(/[^A-Za-z0-9]/g, "")
      .slice(0, maxLength)
      .toUpperCase()
    setPaymentMethod((prev) => ({ ...prev, bankCode: cleaned }))
  }

  const formatClabe = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 18)
    setPaymentMethod((prev) => ({ ...prev, accountNumber: cleaned }))
  }

  const formatIdPassport = (value: string) => {
    const cleaned = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase()
    setPaymentMethod((prev) => ({ ...prev, taxId: cleaned }))
  }

  // --- Country-specific field rendering ---

  const renderCountryFields = () => {
    if (!selectedCountry) return null

    switch (selectedCountry) {
      case "uk":
        return (
          <>
            <div className="flex gap-2" style={{ width: "100%" }}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-sort-code"
                  value={paymentMethod.bankCode || ""}
                  placeholder={lang.account.payment_methods.payment_form.sort_code_placeholder}
                  padding="14px"
                  onChange={(value) => formatSortCode(value)}
                />
              </div>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-account-number"
                  value={paymentMethod.accountNumber || ""}
                  placeholder={lang.account.payment_methods.payment_form.account_number_placeholder}
                  padding="14px"
                  onChange={(value) => formatAccountNumber(value, 8)}
                />
              </div>
            </div>
          </>
        )

      case "us":
        return (
          <>
            <div className="flex gap-2" style={{ width: "100%" }}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-routing-number"
                  value={paymentMethod.bankCode || ""}
                  placeholder={lang.account.payment_methods.payment_form.routing_number_placeholder}
                  padding="14px"
                  onChange={(value) => formatRoutingNumber(value)}
                />
              </div>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-account-number"
                  value={paymentMethod.accountNumber || ""}
                  placeholder={lang.account.payment_methods.payment_form.account_number_placeholder}
                  padding="14px"
                  onChange={(value) => formatAccountNumber(value)}
                />
              </div>
            </div>
          </>
        )

      case "ng":
        return (
          <>
            <KInput
              dataTestId="payment-method-account-number"
              value={paymentMethod.accountNumber || ""}
              placeholder={lang.account.payment_methods.payment_form.nuban_placeholder}
              padding="14px"
              onChange={(value) => formatNUBAN(value)}
            />
            <KDropdown
              height={47}
              placeholder={lang.account.payment_methods.payment_form.select_bank_placeholder}
              options={nigeriaBankOptions}
              onSelect={(selected) => {
                const sel = selected as KSelectOption & { bankCode?: string }
                setPaymentMethod((prev) => ({
                  ...prev,
                  bankName: sel?.value2 || "",
                  bankCode: sel?.bankCode || ""
                }))
              }}
              selected={nigeriaBankOptions.find((opt) => opt.value2 === paymentMethod.bankName)}
            />
          </>
        )

      case "za":
        return (
          <>
            <KDropdown
              height={47}
              placeholder={lang.account.payment_methods.payment_form.select_bank_placeholder}
              options={southAfricaBankOptions}
              onSelect={(selected) =>
                setPaymentMethod((prev) => ({ ...prev, bankName: (selected as KSelectOption)?.value2 || "" }))
              }
              selected={southAfricaBankOptions.find((opt) => opt.value2 === paymentMethod.bankName)}
            />
            <div className="flex gap-2" style={{ width: "100%" }}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-branch-code"
                  value={paymentMethod.bankCode || ""}
                  placeholder={lang.account.payment_methods.payment_form.branch_code_placeholder}
                  padding="14px"
                  onChange={(value) => formatBranchCode(value)}
                />
              </div>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-account-number"
                  value={paymentMethod.accountNumber || ""}
                  placeholder={lang.account.payment_methods.payment_form.account_number_placeholder}
                  padding="14px"
                  onChange={(value) => formatAccountNumber(value)}
                />
              </div>
            </div>
            <KDropdown
              height={47}
              placeholder={lang.account.payment_methods.payment_form.account_type_placeholder}
              options={saAccountTypeOptions}
              onSelect={(selected) =>
                setPaymentMethod((prev) => ({ ...prev, accountType: (selected as KSelectOption)?.value2 }))
              }
              selected={saAccountTypeOptions.find((opt) => opt.value2 === paymentMethod.accountType)}
            />
          </>
        )

      case "au":
        return (
          <>
            <div className="flex gap-2" style={{ width: "100%" }}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-bsb"
                  value={paymentMethod.bankCode || ""}
                  placeholder={lang.account.payment_methods.payment_form.bsb_placeholder}
                  padding="14px"
                  onChange={(value) => formatBSB(value)}
                />
              </div>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-account-number"
                  value={paymentMethod.accountNumber || ""}
                  placeholder={lang.account.payment_methods.payment_form.account_number_placeholder}
                  padding="14px"
                  onChange={(value) => formatAccountNumber(value)}
                />
              </div>
            </div>
          </>
        )

      case "ca":
        return (
          <>
            <div className="flex gap-2" style={{ width: "100%" }}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-institution-transit"
                  value={paymentMethod.bankCode || ""}
                  placeholder={lang.account.payment_methods.payment_form.transit_institution_placeholder}
                  padding="14px"
                  onChange={(value) => formatCanadaTransitInstitution(value)}
                />
              </div>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-account-number"
                  value={paymentMethod.accountNumber || ""}
                  placeholder={lang.account.payment_methods.payment_form.account_number_placeholder}
                  padding="14px"
                  onChange={(value) => formatAccountNumber(value)}
                />
              </div>
            </div>
          </>
        )

      case "in":
        return (
          <>
            <div className="flex gap-2" style={{ width: "100%" }}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-ifsc"
                  value={paymentMethod.bankCode || ""}
                  placeholder={lang.account.payment_methods.payment_form.ifsc_placeholder}
                  padding="14px"
                  onChange={(value) => formatIFSC(value)}
                />
              </div>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-account-number"
                  value={paymentMethod.accountNumber || ""}
                  placeholder={lang.account.payment_methods.payment_form.account_number_placeholder}
                  padding="14px"
                  onChange={(value) => formatAccountNumber(value)}
                />
              </div>
            </div>
          </>
        )

      case "sg":
        return (
          <>
            <div className="flex gap-2" style={{ width: "100%" }}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-bank-branch"
                  value={paymentMethod.bankCode || ""}
                  placeholder={lang.account.payment_methods.payment_form.bank_branch_placeholder}
                  padding="14px"
                  onChange={(value) => formatSGBankBranch(value)}
                />
              </div>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-account-number"
                  value={paymentMethod.accountNumber || ""}
                  placeholder={lang.account.payment_methods.payment_form.account_number_placeholder}
                  padding="14px"
                  onChange={(value) => formatAccountNumber(value)}
                />
              </div>
            </div>
          </>
        )

      case "br": {
        const brBankCode = (paymentMethod.bankCode || "").substring(0, 3)
        const brAgency =
          (paymentMethod.bankCode || "").length >= 4 && (paymentMethod.bankCode || "")[3] === "-"
            ? (paymentMethod.bankCode || "").substring(4)
            : ""
        return (
          <>
            <div className="flex gap-2" style={{ width: "100%" }}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-brazil-bank-code"
                  value={brBankCode}
                  placeholder={lang.account.payment_methods.payment_form.bank_code_placeholder}
                  padding="14px"
                  onChange={(value) => formatBrazilBankCode(value)}
                />
              </div>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-brazil-agency"
                  value={brAgency}
                  placeholder={lang.account.payment_methods.payment_form.agency_placeholder}
                  padding="14px"
                  onChange={(value) => formatBrazilAgency(value)}
                />
              </div>
            </div>
            <div className="flex gap-2" style={{ width: "100%" }}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-account-number"
                  value={paymentMethod.accountNumber || ""}
                  placeholder={lang.account.payment_methods.payment_form.brazil_account_placeholder}
                  padding="14px"
                  onChange={(value) => formatBrazilAccountNumber(value)}
                />
              </div>
              <div style={{ width: "50%" }}>
                <KDropdown
                  height={47}
                  placeholder={lang.account.payment_methods.payment_form.account_type_placeholder}
                  options={brazilAccountTypeOptions}
                  onSelect={(selected) =>
                    setPaymentMethod((prev) => ({ ...prev, accountType: (selected as KSelectOption)?.value2 }))
                  }
                  selected={brazilAccountTypeOptions.find((opt) => opt.value2 === paymentMethod.accountType)}
                />
              </div>
            </div>
            <KInput
              dataTestId="payment-method-tax-id"
              value={paymentMethod.taxId || ""}
              placeholder={lang.account.payment_methods.payment_form.cpf_cnpj_placeholder}
              padding="14px"
              onChange={(value) => formatCPFCNPJ(value)}
            />
          </>
        )
      }

      case "ph":
        return (
          <>
            <KDropdown
              height={47}
              placeholder={lang.account.payment_methods.payment_form.select_bank_placeholder}
              options={philippinesBankOptions}
              onSelect={(selected) =>
                setPaymentMethod((prev) => ({ ...prev, bankName: (selected as KSelectOption)?.value2 || "" }))
              }
              selected={philippinesBankOptions.find((opt) => opt.value2 === paymentMethod.bankName)}
            />
            <KInput
              dataTestId="payment-method-account-number"
              value={paymentMethod.accountNumber || ""}
              placeholder={lang.account.payment_methods.payment_form.account_number_placeholder}
              padding="14px"
              onChange={(value) => formatPHBankCode(value)}
            />
          </>
        )

      case "jp": {
        const jp = splitBankBranch(paymentMethod.bankCode)
        return (
          <>
            <div className="flex gap-2" style={{ width: "100%" }}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-jp-bank-code"
                  value={jp.bank}
                  placeholder={lang.account.payment_methods.payment_form.jp_bank_code_placeholder}
                  padding="14px"
                  onChange={(value) => formatJPBankCode(value)}
                />
              </div>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-jp-branch-code"
                  value={jp.branch}
                  placeholder={lang.account.payment_methods.payment_form.jp_branch_code_placeholder}
                  padding="14px"
                  onChange={(value) => formatJPBranchCode(value)}
                />
              </div>
            </div>
            <KDropdown
              height={47}
              placeholder={lang.account.payment_methods.payment_form.account_type_placeholder}
              options={japanAccountTypeOptions}
              onSelect={(selected) =>
                setPaymentMethod((prev) => ({ ...prev, accountType: (selected as KSelectOption)?.value2 }))
              }
              selected={japanAccountTypeOptions.find((opt) => opt.value2 === paymentMethod.accountType)}
            />
            <KInput
              dataTestId="payment-method-account-number"
              value={paymentMethod.accountNumber || ""}
              placeholder={lang.account.payment_methods.payment_form.jp_account_placeholder}
              padding="14px"
              onChange={(value) => formatAccountNumber(value, 7)}
            />
          </>
        )
      }

      case "mx":
        return (
          <KInput
            dataTestId="payment-method-clabe"
            value={paymentMethod.accountNumber || ""}
            placeholder={lang.account.payment_methods.payment_form.clabe_placeholder}
            padding="14px"
            onChange={(value) => formatClabe(value)}
          />
        )

      case "kr":
        return (
          <div className="flex gap-2" style={{ width: "100%" }}>
            <div style={{ width: "50%" }}>
              <KInput
                dataTestId="payment-method-kr-bank-code"
                value={paymentMethod.bankCode || ""}
                placeholder={lang.account.payment_methods.payment_form.kr_bank_code_placeholder}
                padding="14px"
                onChange={(value) => formatDigitsBankCode(value, 3)}
              />
            </div>
            <div style={{ width: "50%" }}>
              <KInput
                dataTestId="payment-method-account-number"
                value={paymentMethod.accountNumber || ""}
                placeholder={lang.account.payment_methods.payment_form.kr_account_placeholder}
                padding="14px"
                onChange={(value) => formatAccountNumber(value, 14)}
              />
            </div>
          </div>
        )

      case "hk": {
        const hk = splitBankBranch(paymentMethod.bankCode)
        return (
          <>
            <div className="flex gap-2" style={{ width: "100%" }}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-hk-bank-code"
                  value={hk.bank}
                  placeholder={lang.account.payment_methods.payment_form.hk_bank_code_placeholder}
                  padding="14px"
                  onChange={(value) => formatHKBankCode(value)}
                />
              </div>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-hk-branch-code"
                  value={hk.branch}
                  placeholder={lang.account.payment_methods.payment_form.hk_branch_code_placeholder}
                  padding="14px"
                  onChange={(value) => formatHKBranchCode(value)}
                />
              </div>
            </div>
            <KInput
              dataTestId="payment-method-account-number"
              value={paymentMethod.accountNumber || ""}
              placeholder={lang.account.payment_methods.payment_form.hk_account_placeholder}
              padding="14px"
              onChange={(value) => formatAccountNumber(value, 9)}
            />
          </>
        )
      }

      case "my":
        return (
          <>
            <div className="flex gap-2" style={{ width: "100%" }}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-my-bank-code"
                  value={paymentMethod.bankCode || ""}
                  placeholder={lang.account.payment_methods.payment_form.my_bank_code_placeholder}
                  padding="14px"
                  onChange={(value) => formatAlphanumericBankCode(value, 11)}
                />
              </div>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-account-number"
                  value={paymentMethod.accountNumber || ""}
                  placeholder={lang.account.payment_methods.payment_form.my_account_placeholder}
                  padding="14px"
                  onChange={(value) => formatAccountNumber(value, 14)}
                />
              </div>
            </div>
            <KInput
              dataTestId="payment-method-id-passport"
              value={paymentMethod.taxId || ""}
              placeholder={lang.account.payment_methods.payment_form.my_id_passport_placeholder}
              padding="14px"
              onChange={(value) => formatIdPassport(value)}
            />
          </>
        )

      case "id":
        return (
          <div className="flex gap-2" style={{ width: "100%" }}>
            <div style={{ width: "50%" }}>
              <KInput
                dataTestId="payment-method-id-bank-code"
                value={paymentMethod.bankCode || ""}
                placeholder={lang.account.payment_methods.payment_form.id_bank_code_placeholder}
                padding="14px"
                onChange={(value) => formatDigitsBankCode(value, 3)}
              />
            </div>
            <div style={{ width: "50%" }}>
              <KInput
                dataTestId="payment-method-account-number"
                value={paymentMethod.accountNumber || ""}
                placeholder={lang.account.payment_methods.payment_form.id_account_placeholder}
                padding="14px"
                onChange={(value) => formatAccountNumber(value, 15)}
              />
            </div>
          </div>
        )

      case "th":
        return (
          <div className="flex gap-2" style={{ width: "100%" }}>
            <div style={{ width: "50%" }}>
              <KInput
                dataTestId="payment-method-th-bank-code"
                value={paymentMethod.bankCode || ""}
                placeholder={lang.account.payment_methods.payment_form.th_bank_code_placeholder}
                padding="14px"
                onChange={(value) => formatDigitsBankCode(value, 3)}
              />
            </div>
            <div style={{ width: "50%" }}>
              <KInput
                dataTestId="payment-method-account-number"
                value={paymentMethod.accountNumber || ""}
                placeholder={lang.account.payment_methods.payment_form.th_account_placeholder}
                padding="14px"
                onChange={(value) => formatAccountNumber(value, 10)}
              />
            </div>
          </div>
        )

      case "vn":
        return (
          <>
            <div className="flex gap-2" style={{ width: "100%" }}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-vn-bank-code"
                  value={paymentMethod.bankCode || ""}
                  placeholder={lang.account.payment_methods.payment_form.vn_bank_code_placeholder}
                  padding="14px"
                  onChange={(value) => formatAlphanumericBankCode(value, 8)}
                />
              </div>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-account-number"
                  value={paymentMethod.accountNumber || ""}
                  placeholder={lang.account.payment_methods.payment_form.vn_account_placeholder}
                  padding="14px"
                  onChange={(value) => formatAccountNumber(value, 15)}
                />
              </div>
            </div>
            <KInput
              dataTestId="payment-method-branch-name"
              value={paymentMethod.branchName || ""}
              placeholder={lang.account.payment_methods.payment_form.branch_name_placeholder}
              padding="14px"
              onChange={(value) => setPaymentMethod((prev) => ({ ...prev, branchName: value }))}
            />
          </>
        )

      case "tr":
        return (
          <KInput
            dataTestId="payment-method-iban"
            value={paymentMethod.iban}
            placeholder={lang.account.payment_methods.payment_form.iban_tr_placeholder}
            padding="14px"
            onChange={(value) => formatIban(value)}
          />
        )

      case "nz":
        return (
          <KInput
            dataTestId="payment-method-account-number"
            value={paymentMethod.accountNumber || ""}
            placeholder={lang.account.payment_methods.payment_form.nz_account_placeholder}
            padding="14px"
            onChange={(value) => formatNZAccount(value)}
          />
        )

      case "eu":
      default:
        return (
          <KInput
            dataTestId="payment-method-iban"
            value={paymentMethod.iban}
            placeholder={lang.account.payment_methods.payment_form.iban_placeholder}
            padding="14px"
            onChange={(value) => formatIban(value)}
          />
        )
    }
  }

  const showBankNameInput = selectedCountry !== "ng" && selectedCountry !== "za" && selectedCountry !== "ph"
  const showSwiftAndAddress =
    selectedCountry === "tr" ||
    selectedCountry === "uk" ||
    selectedCountry === "eu" ||
    selectedCountry === "ca" ||
    selectedCountry === "nz"

  // --- Country Selection Step ---

  const renderCountryRow = (option: CountryOption) => (
    <button
      key={`${option.code}-${option.label}`}
      onClick={() => {
        handleCountrySelect(option.code)
        setIsCountryDropdownOpen(false)
        setCountrySearch("")
      }}
      className="flex items-center gap-3 w-full px-3 py-2.5 hover:bg-[#F5F5F5] transition-colors cursor-pointer text-left"
      style={{ background: "transparent", border: "none", outline: "none" }}
    >
      <span style={{ fontSize: 22 }}>{option.flag}</span>
      <KSpan text={option.label} color="#111" fontWeight={400} fontSize={14} />
    </button>
  )

  const renderCountrySelection = () => {
    const hasPriority = filteredCountries.priority.length > 0
    const hasOthers = filteredCountries.others.length > 0

    return (
      <div className="pt-8 pb-4 px-6 w-[480px] shadow-md rounded-[10px] bg-[#fff]">
        <div className="flex flex-col items-center gap-6">
          <div className="flex flex-col gap-4 items-center">
            <span
              style={{
                padding: 16,
                borderRadius: "9999px",
                background: "var(--bg-white-0, #FFF)",
                boxShadow:
                  "0px 32px 24px -12px rgba(17, 17, 17, 0.06), 0px 11px 4px 0px rgba(17, 17, 17, 0.01), 0px 6px 4px 0px rgba(17, 17, 17, 0.02), 0px 3px 3px 0px rgba(17, 17, 17, 0.03), 0px 1px 1px 0px rgba(17, 17, 17, 0.04), 0px 0px 0px 1px rgba(17, 17, 17, 0.04)"
              }}
            >
              <img className="w-8 h-8" src="/account_icons/wallet-filled.svg" />
            </span>
            <KTitleSpan
              text={lang.account.payment_methods.payment_form.select_country_title}
              fontSize={24}
              lineHeight="28px"
              color="#000"
            />
            <KSpan
              text={lang.account.payment_methods.payment_form.select_country_description}
              color="#666"
              fontSize={14}
            />
          </div>
          <div ref={dropdownRef} className="w-full relative">
            <div
              className="flex items-center gap-2 w-full rounded-[10px] px-3 py-3"
              style={{
                background: "#F5F5F5",
                border: isCountryDropdownOpen ? "1px solid #111" : "1px solid transparent",
                transition: "border-color 0.15s"
              }}
              onClick={() => setIsCountryDropdownOpen(true)}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ opacity: 0.4, flexShrink: 0 }}>
                <path
                  d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9"
                  stroke="#111"
                  strokeWidth="1.33"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                type="text"
                value={countrySearch}
                onChange={(e) => {
                  setCountrySearch(e.target.value)
                  setIsCountryDropdownOpen(true)
                }}
                onFocus={() => setIsCountryDropdownOpen(true)}
                placeholder={lang.account.payment_methods.payment_form.select_country_placeholder}
                style={{
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  width: "100%",
                  fontFamily: "Inter, sans-serif",
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: "20px",
                  letterSpacing: "-0.084px",
                  color: "#111",
                  caretColor: "#111"
                }}
              />
            </div>
            {isCountryDropdownOpen && (
              <div
                className="absolute left-0 right-0 mt-1 rounded-[10px] overflow-hidden z-50"
                style={{
                  background: "#FFF",
                  boxShadow: "0px 12px 24px -4px rgba(17, 17, 17, 0.12), 0px 0px 0px 1px rgba(17, 17, 17, 0.04)",
                  maxHeight: 320,
                  overflowY: "auto"
                }}
              >
                {hasPriority && <div>{filteredCountries.priority.map(renderCountryRow)}</div>}
                {hasPriority && hasOthers && <div style={{ borderTop: "1px solid #E5E5E5", margin: "4px 0" }} />}
                {hasOthers && <div>{filteredCountries.others.map(renderCountryRow)}</div>}
                {!hasPriority && !hasOthers && (
                  <div className="px-3 py-4 text-center">
                    <KSpan
                      text={lang.account.payment_methods.payment_form.no_countries_found}
                      color="#999"
                      fontSize={14}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex mt-4 w-full">
            <KButton
              dataTestId="payment-method-cancel"
              text={lang.common.cancel}
              height="48px"
              onClick={() => {
                // Closed with whichever setter the dialog was opened with. A form opened over the
                // co-manager to change a saved account is given the edit one, and it starts here.
                if (setAddPaymentMethod !== undefined) {
                  setAddPaymentMethod(false)
                } else if (setEdit !== undefined) {
                  setEdit(false)
                }
              }}
              shadowDisabled={true}
              background="#fff"
              textColor="#000"
            />
          </div>
        </div>
      </div>
    )
  }

  // --- Payment Form Step ---

  const renderPaymentForm = () => {
    return (
      <div className="pt-8 pb-4 px-6 w-[480px] shadow-md rounded-[10px] bg-[#fff]">
        <div className="flex flex-col items-center gap-8">
          <div className="flex flex-col gap-8 items-center">
            <span
              style={{
                padding: 16,
                borderRadius: "9999px",
                background: "var(--bg-white-0, #FFF)",
                boxShadow:
                  "0px 32px 24px -12px rgba(17, 17, 17, 0.06), 0px 11px 4px 0px rgba(17, 17, 17, 0.01), 0px 6px 4px 0px rgba(17, 17, 17, 0.02), 0px 3px 3px 0px rgba(17, 17, 17, 0.03), 0px 1px 1px 0px rgba(17, 17, 17, 0.04), 0px 0px 0px 1px rgba(17, 17, 17, 0.04)"
              }}
            >
              <img className="w-8 h-8" src="/account_icons/wallet-filled.svg" />
            </span>
            <KTitleSpan text={title || ""} fontSize={24} lineHeight="28px" color="#000" />
          </div>
          <div className="flex flex-col gap-4 items-center w-full ">
            {showBankNameInput && (
              <KInput
                dataTestId="payment-method-title"
                value={paymentMethod.bankName}
                placeholder={lang.account.payment_methods.payment_form.title_placeholder}
                padding="14px"
                onChange={(value) => {
                  setPaymentMethod({ ...paymentMethod, bankName: value })
                }}
              />
            )}
            {renderCountryFields()}
            <KInput
              dataTestId="payment-method-account-holder-name"
              value={paymentMethod.accountHolderName}
              placeholder={lang.account.payment_methods.payment_form.account_holder_name_placeholder}
              padding="14px"
              onChange={(value) => {
                setPaymentMethod({ ...paymentMethod, accountHolderName: value })
              }}
            />
            <div className="flex gap-2" style={{ width: "100%" }}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="payment-method-swift-code"
                  value={paymentMethod.swiftCode || ""}
                  placeholder={
                    showSwiftAndAddress
                      ? selectedCountry === "ca"
                        ? lang.account.payment_methods.payment_form.swift_bic_code_optional_placeholder
                        : selectedCountry === "nz"
                        ? lang.account.payment_methods.payment_form.swift_bic_nz_placeholder
                        : lang.account.payment_methods.payment_form.switf_bic_code_placeholder
                      : lang.account.payment_methods.payment_form.swift_bic_code_optional_placeholder
                  }
                  padding="14px"
                  onChange={(value) => {
                    setPaymentMethod({ ...paymentMethod, swiftCode: value })
                  }}
                />
              </div>
              <div style={{ width: "50%" }}>
                <KDropdown
                  dataTestId="payment-method-currency"
                  height={47}
                  placeholder={lang.account.payment_methods.payment_form.currency}
                  options={currencyOptions}
                  onSelect={(selected) =>
                    setPaymentMethod({ ...paymentMethod, currency: (selected as KSelectOption)?.value2 })
                  }
                  selected={currencyOptions.find((option) => option.value2 === paymentMethod.currency)}
                />
              </div>
            </div>
            <Warning warning={lang.account.payment_methods.payment_form.warning_text} />
            <div className="flex mt-8 gap-3" style={{ width: "100%" }}>
              <KButton
                dataTestId="payment-method-cancel"
                text={lang.common.cancel}
                height="48px"
                onClick={() => {
                  // A form reached through the country step goes back to it rather than closing, so a
                  // wrong country costs the artist one press. Editing a saved account has no step
                  // behind it, there cancel closes the dialog.
                  if (!isEditing) {
                    setSelectedCountry(null)
                    return
                  }
                  if (addPaymentMethod !== undefined && setAddPaymentMethod !== undefined) {
                    setAddPaymentMethod(false)
                  } else if (setEdit !== undefined) {
                    setEdit(false)
                  }
                }}
                shadowDisabled={true}
                background="#fff"
                textColor="#000"
              />
              <KButton
                dataTestId="payment-method-accept"
                background="#000"
                textColor="#FFF"
                height="48px"
                text={lang.common.accept}
                onClick={() => {
                  const paymentData = { ...paymentMethod, countryType: selectedCountry || "eu" }

                  const errors = paymentMethodValidator(paymentData, selectedCountry || "eu")
                  if (Object.keys(errors).length > 0) return

                  /*if (paymentData.swiftCode === "") {
                    delete paymentData.swiftCode
                  }*/
                  if (!paymentData.iban) {
                    delete paymentData.iban
                  }
                  if (!paymentData.accountNumber) {
                    delete paymentData.accountNumber
                  }
                  if (!paymentData.accountType) {
                    delete paymentData.accountType
                  }
                  if (!paymentData.bankCode) {
                    delete paymentData.bankCode
                  }
                  if (!paymentData.branchName) {
                    delete paymentData.branchName
                  }
                  if (!paymentData.taxId) {
                    delete paymentData.taxId
                  }

                  if (edit === undefined && addPaymentMethod !== undefined && setAddPaymentMethod !== undefined) {
                    createPaymentMethod(paymentData, adminPanelUserId)
                    onSubmit?.(paymentData)
                    setAddPaymentMethod(false)
                  } else if (edit !== undefined && setEdit !== undefined && addPaymentMethod === undefined) {
                    updatePaymentMethod(paymentData)
                    onSubmit?.(paymentData)
                    setEdit(false)
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show country selection for new accounts, form for editing or after country is selected
  if (!isEditing && !selectedCountry) {
    return renderCountrySelection()
  }

  return renderPaymentForm()
}

export default connect(null, { createPaymentMethod, deletePaymentMethod, updatePaymentMethod })(PaymentForm)
